// Admin Tracking dashboard — the predictive-delay surface.
//
// Six tabs, URL-driven so each is bookmarkable:
//
//   /admin/tracking            → Overview (default)
//   /admin/tracking/in-flight  → In-flight (the workhorse table)
//   /admin/tracking/at-risk    → At-risk (Phase 3 — predicted delays)
//   /admin/tracking/lanes      → Lanes (Phase 2 — origin→dest baselines)
//   /admin/tracking/carriers   → Carriers (Phase 2 — per-carrier scorecards)
//   /admin/tracking/exceptions → Exceptions (operator follow-up list)
//
// The Phase-2/3 tabs render a "still collecting baselines" stub so
// they're visible in nav and the architecture story is clear, even
// though they don't have live data yet.

import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard, Plane, AlertTriangle, Route, Truck, OctagonAlert, Users,
} from 'lucide-react'
import OverviewTab from '../../components/tracking/TrackingOverviewTab'
import InFlightTab from '../../components/tracking/TrackingInFlightTab'
import ExceptionsTab from '../../components/tracking/TrackingExceptionsTab'
import AtRiskTab from '../../components/tracking/TrackingAtRiskTab'
import LanesTab from '../../components/tracking/TrackingLanesTab'
import CarriersTab from '../../components/tracking/TrackingCarriersTab'
import WorkerStatusCard from '../../components/admin/WorkerStatusCard'
import { getTrackingWorkerStatus, getTrackingClients } from '../../lib/api'

const TABS = [
  { key: 'overview',   label: 'Overview',   icon: LayoutDashboard, Component: OverviewTab },
  { key: 'in-flight',  label: 'In-flight',  icon: Plane,           Component: InFlightTab },
  { key: 'at-risk',    label: 'At-risk',    icon: AlertTriangle,   Component: AtRiskTab },
  { key: 'lanes',      label: 'Lanes',      icon: Route,           Component: LanesTab },
  { key: 'carriers',   label: 'Carriers',   icon: Truck,           Component: CarriersTab },
  { key: 'exceptions', label: 'Exceptions', icon: OctagonAlert,    Component: ExceptionsTab },
]

const DEFAULT_TAB = 'overview'

// Global client filter — lives in the page shell (above the tabs) so
// picking a client narrows Overview, In-flight, and Exceptions in
// lock-step. State persists in the URL as ?client_id= and is read
// independently by each tab via useSearchParams.
function ClientFilter() {
  const [params, setParams] = useSearchParams()
  const clientId = params.get('client_id') || ''
  const setClientId = (id) => {
    const next = new URLSearchParams(params)
    if (id) next.set('client_id', id)
    else    next.delete('client_id')
    // Wipe any tab-level page param when the client changes — the row
    // counts shift, so page 3 of "all clients" rarely makes sense as
    // page 3 of "one client".
    next.delete('page')
    setParams(next, { replace: true })
  }

  const { data, isLoading } = useQuery({
    queryKey: ['tracking', 'clients'],
    queryFn: () => getTrackingClients({ days: 90 }),
    staleTime: 5 * 60_000,    // dropdown shape doesn't change often
  })
  // Sort order is preserved from the API (busiest first) so the most
  // common picks rise to the top of the dropdown — we just hide the
  // counts in the label.
  const clients = data?.clients || []

  return (
    <div className="card px-4 py-2.5 flex items-center gap-2 flex-wrap">
      <Users size={14} className="text-slate-400 flex-shrink-0" />
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Client filter</span>
      <select
        value={clientId}
        onChange={e => setClientId(e.target.value || undefined)}
        disabled={isLoading}
        className="text-sm px-2 py-1 border border-slate-200 rounded bg-white min-w-[260px] max-w-[480px] flex-1"
      >
        <option value="">All clients</option>
        {clients.map(c => (
          <option key={c.client_id} value={c.client_id}>{c.name}</option>
        ))}
      </select>
      {clientId && (
        <button
          onClick={() => setClientId(undefined)}
          className="text-xs text-slate-500 hover:text-red-600"
          title="Clear client filter"
        >
          Clear
        </button>
      )}
    </div>
  )
}

export default function AdminTracking() {
  const navigate = useNavigate()
  const { tab: tabParam } = useParams()
  // Read the current URL search so tab clicks can preserve global
  // filters like ?client_id= across navigation. Without this, switching
  // from Overview → In-flight while a client is selected would strip
  // the filter mid-flow.
  const [searchParams] = useSearchParams()
  const search = searchParams.toString()
  const searchSuffix = search ? `?${search}` : ''
  const activeKey = TABS.find(t => t.key === tabParam)?.key ?? DEFAULT_TAB
  const Active = TABS.find(t => t.key === activeKey).Component

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* Page header */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Tracking</h1>
          <p className="text-sm text-slate-500 mt-1">
            Live shipment timelines, exception triage, and (soon) predicted delays.
          </p>
        </div>
      </div>

      {/* Worker heartbeat + global client filter — stacked at the top
          so they're visible from every tab. Worker first (operational
          health), then the filter (scope-of-data control). */}
      <div className="mb-5 space-y-3">
        <WorkerStatusCard
          workerName="tracking-poll"
          queryKey={['tracking', 'worker-status']}
          queryFn={getTrackingWorkerStatus}
          stalePathHint="DigitalOcean → Workers → tracking-poll"
          statsConfig={[
            { key: 'events_inserted', label: 'events', accent: 'emerald' },
            { key: 'polled',          label: 'polled', accent: 'slate'   },
            { key: 'cycles',          label: 'runs',   accent: 'slate'   },
          ]}
        />
        <ClientFilter />
      </div>

      {/* Tabs strip */}
      <div className="border-b border-slate-200 mb-5">
        <nav className="flex flex-wrap gap-1 -mb-px">
          {TABS.map(t => {
            const Icon = t.icon
            const isActive = t.key === activeKey
            return (
              <button
                key={t.key}
                onClick={() => navigate(
                  (t.key === DEFAULT_TAB ? '/admin/tracking' : `/admin/tracking/${t.key}`) + searchSuffix
                )}
                className={[
                  'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 transition-colors',
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                ].join(' ')}
              >
                <Icon size={15} />
                {t.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Active tab content */}
      <Active />
    </div>
  )
}
